import Image from "next/image";
import categoriesJson from "./json/categories.json";
import { useTranslation } from "@/app/i18n/client";
import arrowRight from "@/assets/images/icons/whiteArrow.svg";
import ActivityCard from "@/common/customElements/ActivityCard";
import {
  CircularDiv,
  CustomDiv,
  InlineDiv,
  VerticalCenterDiv,
} from "@/common/divs";
import { SemiBoldText } from "@/common/texts";
import { useScroll } from "@/hooks/useScroll";
import { ActivityDetailGeoSearch, ActivitySubCategory } from "@/model";
import { PRIMARY_BUTTON_COLOR } from "@/utils/constants";
import { getCoverImage, renderCategoryIcon } from "@/utils/functions";

export interface ActivitiesListProps {
  activities: Array<ActivityDetailGeoSearch>;
  title: string;
  imInMapView?: boolean;
  locations?: {
    lat: number;
    lng: number;
    ref: React.RefObject<any>;
    subcategory: ActivitySubCategory;
    categoryId: number;
  }[];
  isMobile?: boolean;
  inFavoriteList?: boolean;
  categoryId?: number;
  trackEvent?: (event: {
    name: string;
    value?: string | number;
    properties: object;
  }) => void | undefined;
  TrackCustomEvents?:
    | {
        EVENT_BROWSED: string;
        EVENT_SEARCHED: string;
        EVENT_PURCHASED: string;
        EVENT_REGISTERED_ACCOUNT: string;
        EVENT_ADD_TO_WISHLIST: string;
        EVENT_ADD_PAYMENT_INFO: string;
        EVENT_SHARED_PRODUCT: string;
        EVENT_FIRST_REGION_CHOSEN: string;
        EVENT_FIRST_CATEGORIES_CHOSEN: string;
        EVENT_ACTIVE_REGION: string;
        EVENT_ACTIVE_CATEGORIES: string;
        EVENT_BROWSED_WEB: string;
        EVENT_CHECKOUT_WEB: string;
        EVENT_SEARCHED_WEB: string;
        EVENT_PURCHASED_WEB: string;
        EVENT_REGISTERED_ACCOUNT_WEB: string;
        EVENT_ADD_TO_WISHLIST_WEB: string;
        EVENT_ADD_PAYMENT_INFO_WEB: string;
        EVENT_SHARED_PRODUCT_WEB: string;
        EVENT_FIRST_REGION_CHOSEN_WEB: string;
        EVENT_FIRST_CATEGORIES_CHOSEN_WEB: string;
        EVENT_ACTIVE_REGION_WEB: string;
        EVENT_ACTIVE_CATEGORIES_WEB: string;
      }
    | undefined;
}

const ActivitiesList: React.FC<ActivitiesListProps> = ({
  activities,
  title,
  imInMapView,
  locations,
  isMobile,
  inFavoriteList = false,
  categoryId,
  trackEvent,
  TrackCustomEvents,
}) => {
  const { element, scrollBy, showRightArrow, showLeftArrow, SCROLL_WIDTH } =
    useScroll();
  const { t } = useTranslation();
  const parseTitle = () => {
    const category = categoriesJson.find(
      (category) => category.name.toLowerCase() === title.toLowerCase(),
    );
    if (category) {
      return t(category.i18_name);
    } else {
      return t(title);
    }
  };

  return (
    <CustomDiv
      className={
        imInMapView ?
          "activities-list-items-container no-padding-map-view"
        : "activities-list-items-container"
      }
      paddingY="15px"
    >
      <InlineDiv>
        <SemiBoldText fontSize="24px" style={{ marginBottom: "16px" }}>
          {parseTitle()}
        </SemiBoldText>
        {categoryId && (
          <CustomDiv
            background={"#3c3c3b"}
            style={{
              borderRadius: "12px",
              alignContent: "center",
              alignItems: "center",
              justifyContent: "center",
              display: "flex",
              height: "30px",
              width: "30px",
              marginTop: "5px",
              marginLeft: "5px",
            }}
          >
            <Image
              style={{ marginBottom: "3px", width: "16px", height: "16px" }}
              src={renderCategoryIcon(categoryId)}
              alt={t(title)}
              width={16}
              height={16}
            ></Image>
          </CustomDiv>
        )}
      </InlineDiv>
      <InlineDiv
        ref={element}
        className={"activityCarousel"}
        style={{
          gap: "24px",
          overflowX: "auto",
          flexWrap:
            isMobile ? "nowrap"
            : imInMapView ? "wrap"
            : "nowrap",
        }}
      >
        {activities?.map((activity, index) => (
          <ActivityCard
            imInMapView={imInMapView}
            key={activity.id}
            imageUrl={activity.images ? getCoverImage(activity.images) : ""}
            title={activity.name}
            price={
              activity.priceWithTaxFormatted ?
                activity.priceWithTaxFormatted
              : "0"
            }
            tag={inFavoriteList ? activity.category?.name : parseTitle()}
            inFavoriteList={inFavoriteList}
            id={activity.id.toString()}
            isFavorite={activity.isFavorite ?? false}
            location={locations ? locations[index] : undefined}
            activity={activity}
            trackEvent={trackEvent}
            TrackCustomEvents={TrackCustomEvents}
          />
        ))}
      </InlineDiv>
      {window.innerWidth > 450 ?
        showLeftArrow && (
          <VerticalCenterDiv left="20px">
            <CircularDiv
              background={PRIMARY_BUTTON_COLOR}
              width="56px"
              height="56px"
              paddingX="11px"
              className="carousel-button"
              onClick={() => {
                scrollBy({ left: -SCROLL_WIDTH, behavior: "smooth" });
              }}
            >
              <Image
                width={12}
                height={19}
                src={arrowRight}
                style={{ rotate: "180deg", width: "12px", height: "19px" }}
                alt="arrowRight"
              />
            </CircularDiv>
          </VerticalCenterDiv>
        )
      : null}
      {window.innerWidth > 450 ?
        showRightArrow && (
          <VerticalCenterDiv right="20px">
            <CircularDiv
              background={PRIMARY_BUTTON_COLOR}
              width="56px"
              height="56px"
              paddingX="11px"
              className="carousel-button"
              onClick={() => {
                scrollBy({ left: SCROLL_WIDTH, behavior: "smooth" });
              }}
            >
              <Image
                width={12}
                height={19}
                src={arrowRight}
                alt="arrowRight"
                style={{ width: "12px", height: "19px" }}
              />
            </CircularDiv>
          </VerticalCenterDiv>
        )
      : null}
    </CustomDiv>
  );
};

export default ActivitiesList;
